import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useTakeProfitSetIsModifyingField,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTakeProfitErrors, selectTakeProfitTile } from '@/store/orders/takeProfitOrder/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const TakeProfitExpiry = () => {
  const tileId = useContext(TakeProfitContext).tileId;
  const setIsModifyingField = useTakeProfitSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, formatTime, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const { expiry } = useAppSelector((state) => {
    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} not found in state.`);
      return {};
    }
    if (tile.type !== 'TakeProfitOrder') {
      console.error(`[TakeProfitSpot] Tile with id ${tileId} is not a TakeProfitOrder.`);
      return {};
    }

    return { expiry: tile.properties.expiry };
  }, deepEqual);

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));

  const validateTakeProfit = useValidateTakeProfit();

  const handleIsGoodTillCancelledChange = (isGoodTillConcelled: boolean) => {
    track('Order action', 'Good till cancelled changed');
    validateTakeProfit({
      expiry: isGoodTillConcelled
        ? { type: 'GoodTillCancelled' }
        : { type: 'ExpiryDate', date: undefined, time: undefined },
    });
  };

  const handleDateChange = (date: string) => {
    track('Order action', 'Expiry date changed');
    if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
      return;
    }

    validateTakeProfit({
      expiry: {
        type: 'ExpiryDate',
        date,
        time: expiry.time, // Keep the existing time if it exists
      },
    });
  };

  const handleTimeChange = ({
    hour = 0,
    minute = 0,
    second = 0,
    period,
  }: {
    hour?: number;
    minute?: number;
    second?: number;
    period?: 'AM' | 'PM';
  }) => {
    track('Order action', 'Expiry time changed');
    if (isNotDefined(expiry) || expiry.type === 'GoodTillCancelled') {
      return;
    }

    const hour24 = period === 'PM' && hour < 12 ? hour + 12 : hour;

    const newExpiry = {
      type: 'ExpiryDate' as const,
      date: expiry.date, // Keep the existing date from ref
      time: formatTime(new Date(0, 0, 0, hour24, minute, second), { timeStyle: 'medium', hour12: false }),
    };

    validateTakeProfit({ expiry: newExpiry });
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const expiryDateError = errors?.expiryDay;
  const expiryTimeError = errors?.expiryTime;

  return (
    <OrderRow labelId="order.fields.expiry.label" alignTop>
      <Checkbox
        dataE2e="GTC-checkbox"
        checked={expiry?.type === 'GoodTillCancelled'}
        onCheckBoxChange={handleIsGoodTillCancelledChange}
        label={formatMessage({ id: 'order.fields.expiry.goodTillCanceled' })}
        size="sm"
      />

      {expiry?.type === 'ExpiryDate' && (
        <div
          className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
          data-e2e="expiry-date"
        >
          <OmsFieldErrorWrapper errors={expiryDateError}>
            <SgDatePicker
              size="sm"
              yearControls
              value={expiry?.date}
              placeholder="Expiry date"
              locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
              dateInputCultureInfo={getComputedLocale()}
              onChange={handleDateChange}
              onFocus={() => setIsModifyingField(true)}
              onBlurInput={() => setIsModifyingField(false)}
              showClearButton={false}
              validationState={isDefined(expiryDateError) ? 'invalid' : undefined}
              dateFormat={DATE_FORMAT_TEXT}
            />
          </OmsFieldErrorWrapper>

          <OmsFieldErrorWrapper errors={expiryTimeError}>
            <SgTimepicker
              size="sm"
              appendTo="body"
              withSeconds
              value={expiry?.time ? expiry?.time : undefined}
              onTimeSelected={(e) => handleTimeChange(e.detail)}
              state={isDefined(expiryTimeError) ? 'invalid' : undefined}
            />
          </OmsFieldErrorWrapper>

          {/*<HStack gap="4px" verticalAlignment="center">*/}
          {/*  <SgTimePicker size="sm" onTimeSelected={handleTimeChange} />*/}
          {/*  <Tooltip contents="Hello" position="right">*/}
          {/*    <i className="icon text-secondary icon-sm">info</i>*/}
          {/*  </Tooltip>*/}
          {/*</HStack>*/}
        </div>
      )}
    </OrderRow>
  );
};
